/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { useEffect, useMemo, useSyncExternalStore } from "react";
import { useAiEventStreamStore } from "../provider/AiEventStreamProvider";
import { AiEventStreamEvent, AiEventStreamSnapshot } from "../types/AiEventStream";

export function useAiEventStreamSnapshot(streamKey: number | null = null): AiEventStreamSnapshot {
  const store = useAiEventStreamStore(streamKey);

  return useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
}

export function useAiEventStreamControls(streamKey: number | null = null) {
  const store = useAiEventStreamStore(streamKey);

  return useMemo(
    () => ({
      connect: store.connect,
      close: store.close,
      reset: store.reset,
      getCurrentStreamId: store.getCurrentStreamId,
      completePromptGeneration: store.completePromptGeneration,
    }),
    [store]
  );
}

export function useAiPromptGenerationStream(streamKey: number | null = null) {
  const snapshot = useAiEventStreamSnapshot(streamKey);

  const promptGeneration = snapshot.promptGeneration ?? {
    text: "",
    deltas: [],
    lastDelta: null,
    eventCount: 0,
    isComplete: false,
    model: null,
    contentType: "markdown",
    completionSource: null,
  };

  return {
    text: promptGeneration.text ?? "",
    deltas: promptGeneration.deltas ?? [],
    lastDelta: promptGeneration.lastDelta ?? null,
    eventCount: promptGeneration.eventCount ?? 0,
    isComplete: promptGeneration.isComplete ?? false,
    model: promptGeneration.model ?? null,
    contentType: promptGeneration.contentType ?? "markdown",
    completionSource: promptGeneration.completionSource ?? null,
    status: snapshot.status,
    error: snapshot.error,
    info: snapshot.info,
    streamId: snapshot.streamId,
  };
}

export function useAiEventStreamLifecycle(streamKey: number | null = null) {
  const snapshot = useAiEventStreamSnapshot(streamKey);
  const hasVisibleText = (snapshot.promptGeneration.text ?? "").length > 0;
  const hasStreamStarted = snapshot.streamId !== null && snapshot.status !== "idle" && snapshot.status !== "connecting";
  const hasStreamEnded = snapshot.status === "completed" || snapshot.status === "error";

  return {
    hasStreamStarted,
    hasStreamEnded,
    isWaitingForFirstChunk: hasStreamStarted && !hasStreamEnded && !hasVisibleText,
    isStreamingText: hasVisibleText && !hasStreamEnded,
  };
}

export function useAiGraphNodeStream(streamKey: number | null = null) {
  const snapshot = useAiEventStreamSnapshot(streamKey);

  return {
    ...snapshot.graphNode,
    status: snapshot.status,
    streamId: snapshot.streamId,
  };
}

export function useAiEventStreamEventHandler<TEvent extends AiEventStreamEvent = AiEventStreamEvent>(
  eventType: string,
  handler: (event: TEvent) => void | Promise<void>,
  streamKey: number | null = null
): void {
  const store = useAiEventStreamStore(streamKey);

  useEffect(() => {
    return store.addEventListener(eventType, handler as (event: AiEventStreamEvent) => void | Promise<void>);
  }, [eventType, handler, store]);
}
